// Personal records, shared by the progress history and the end of a guided session.
const LB_TO_KG = 0.45359237;

// "Supino (Aquecimento)" and "Bench Press (Warm-up)" are the same movement as "Supino" / "Bench Press"
export const getBaseName = (name) => (name || '').replace(/\s*\((warm-?up|aquecimento|feeder|preparo)\)/gi, '').trim();

// Records compare in kg, whatever unit each set was logged in
export const toKg = (weight, unit) => (unit === 'lbs' || unit === 'lb' ? weight * LB_TO_KG : weight);

export const epley1RM = (weight, reps) => (reps <= 0 || weight <= 0 ? 0 : reps === 1 ? weight : weight * (1 + reps / 30));

export const readSet = (set) => ({
  weight: parseFloat(set.weight) || 0,
  unit: set.weightUnit || 'kg',
  reps: parseInt(set.reps) || 0,
});

// Best weight and best estimated 1RM per exercise in one session, with the set that set each
const sessionBests = (exercises) => {
  const bests = {};
  (exercises || []).forEach((exercise) => {
    const baseName = getBaseName(exercise.name);
    (exercise.sets || []).forEach((rawSet) => {
      const set = readSet(rawSet);
      if (set.weight <= 0 || set.reps <= 0) return;
      const kg = toKg(set.weight, set.unit);
      const oneRM = epley1RM(kg, set.reps);
      const current = bests[baseName] || { weight: 0, oneRM: 0 };
      bests[baseName] = {
        weight: Math.max(current.weight, kg),
        oneRM: Math.max(current.oneRM, oneRM),
        weightSet: kg > current.weight ? set : current.weightSet,
        oneRMSet: oneRM > current.oneRM ? set : current.oneRMSet,
      };
    });
  });
  return bests;
};

const byDate = (a, b) => new Date(a.attributes?.completedAt) - new Date(b.attributes?.completedAt);

/**
 * Records per log: { [logId]: { [baseName]: { weight, oneRM } } }.
 * A record only counts when a session beats the best of the sessions before it,
 * so a first session (or a set beating an earlier set of the same day) isn't one.
 */
export const computeRecords = (logs) => {
  const best = {};
  const result = {};
  [...logs].sort(byDate).forEach((log) => {
    const records = {};
    Object.entries(sessionBests(log.attributes?.exercises)).forEach(([baseName, session]) => {
      const previous = best[baseName];
      if (previous) {
        const weight = session.weight > previous.weight;
        const oneRM = session.oneRM > previous.oneRM;
        if (weight || oneRM) records[baseName] = { weight, oneRM };
      }
      best[baseName] = {
        weight: Math.max(previous?.weight || 0, session.weight),
        oneRM: Math.max(previous?.oneRM || 0, session.oneRM),
      };
    });
    result[log.id] = records;
  });
  return result;
};

/**
 * Records a session that just ended sets against every earlier log:
 * [{ name, kind: 'weight' | 'oneRM', set: { weight, unit, reps }, previousKg }]
 */
export const findNewRecords = (previousLogs, sessionExercises) => {
  const best = {};
  (previousLogs || []).forEach((log) => {
    Object.entries(sessionBests(log.attributes?.exercises)).forEach(([baseName, session]) => {
      const current = best[baseName] || { weight: 0, oneRM: 0 };
      best[baseName] = { weight: Math.max(current.weight, session.weight), oneRM: Math.max(current.oneRM, session.oneRM) };
    });
  });
  return Object.entries(sessionBests(sessionExercises)).flatMap(([name, session]) => {
    const previous = best[name];
    if (!previous) return [];
    if (session.weight > previous.weight) return [{ name, kind: 'weight', set: session.weightSet, previousKg: previous.weight }];
    if (session.oneRM > previous.oneRM) return [{ name, kind: 'oneRM', set: session.oneRMSet, previousKg: previous.weight }];
    return [];
  });
};
